import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  ConnectionLineType,
  useNodesState,
  useEdgesState,
  Panel,
  MarkerType,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useData } from '../context/DataContext';
import { Button } from "./ui/button";
import { ZoomIn, ZoomOut, RefreshCw, ArrowDown } from 'lucide-react';
import dagre from 'dagre';
import { getDepartmentCode } from '../utils/helpers';

// Define interface for PersonNode data
interface PersonNodeData {
  name: string;
  jobRole: string;
  primaryDepartment: string;
  additionalDepartments?: string[];
  managerId?: string;
}

interface PersonNodeProps {
  data: PersonNodeData;
}

// Custom node component to display person information
const PersonNode: React.FC<PersonNodeProps> = ({ data }) => {
  return (
    <div className="bg-white border border-border rounded-md shadow-sm p-3 w-[240px]">
      <div className="font-medium text-primary">{data.name}</div>
      <div className="text-sm text-muted-foreground mt-1">{data.jobRole}</div>

      {/* Primary Department */}
      <div className="mt-2">
        <h4 className="font-medium text-xs">Primary Department</h4>
        <p className="text-xs text-muted-foreground bg-secondary/20 py-1 px-2 rounded mt-1 inline-block">
          {data.primaryDepartment || 'None'}
        </p>
      </div>

      {/* Additional Departments */}
      {data.additionalDepartments && data.additionalDepartments.length > 0 && (
        <div className="mt-2">
          <h4 className="font-medium text-xs">Additional Departments</h4>
          <div className="flex flex-wrap gap-1 mt-1">
            {data.additionalDepartments.map((dept, index) => (
              <span key={index} className="text-xs text-muted-foreground bg-accent py-1 px-2 rounded inline-block">
                {dept}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add source and target handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="target"
        style={{ background: '#000' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source"
        style={{ background: '#000' }}
      />
    </div>
  );
};

// Define interface for ReportingEdge props
interface ReportingEdgeProps {
  id: string;
  source: string;
  target: string;
  style?: React.CSSProperties;
}

// Custom edge with label
const ReportingEdge: React.FC<ReportingEdgeProps> = ({ id, source, target, style = {} }) => {
  return (
    <div className="react-flow__edge">
      <div
        className="react-flow__edge-path"
        style={{
          ...style,
          strokeWidth: 2,
          stroke: 'var(--primary)',
          strokeDasharray: '5,5',
          animation: 'flowPathAnimation 1s infinite linear',
        }}
      />
      <div className="react-flow__edge-label" style={{ fontSize: '10px', background: 'white', padding: '2px 4px', borderRadius: '4px' }}>
        Reports to
      </div>
    </div>
  );
};

// Register custom node and edge types
const nodeTypes = {
  person: PersonNode,
};

// Dagre layout configuration
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

// Helper function to get node width and height
const getNodeDimensions = () => {
  return { width: 240, height: 130 }; // Adjusted dimensions for the expanded node
};

type LayoutDirection = 'TB' | 'LR';

// Helper function to organize nodes using dagre
const getLayoutedElements = (nodes: Node[], edges: Edge[], direction: LayoutDirection = 'TB') => {
  const { width, height } = getNodeDimensions();

  dagreGraph.setGraph({ rankdir: direction, nodesep: 80, ranksep: 100 });

  // Add nodes to the graph
  nodes.forEach((node: Node) => {
    dagreGraph.setNode(node.id, { width, height });
  });

  // Add edges to the graph
  edges.forEach((edge: Edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Calculate the layout
  dagre.layout(dagreGraph);

  // Retrieve positions from dagre
  const layoutedNodes = nodes.map((node: Node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

// Custom style for edges
const getEdgeStyle = () => {
  return {
    stroke: '#000000',
    strokeWidth: 3,
    zIndex: 1000,
  };
};

export const OrganizationChart = () => {
  const { users, departments } = useData();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [initialized, setInitialized] = useState(false);

  // Process data to create nodes and edges
  const processData = useCallback(() => {
    if (!users || users.length === 0 || !departments) {
      return;
    }

    // Create a map of departmentId to department name
    const departmentMap: Record<string, string> = departments.reduce((acc, dept) => {
      acc[dept.id] = dept.departmentName;
      acc[dept.departmentCode] = dept.departmentName; // Also map by department code
      return acc;
    }, {} as Record<string, string>);

    // Filter out archived users
    const activeUsers = users.filter(user => !user.archived);

    // Create nodes first
    const personNodes = activeUsers.map(person => {
      const nodeId = String(person.id);

      return {
        id: nodeId,
        type: 'person',
        data: {
          name: person.name,
          jobRole: person.jobRole,
          primaryDepartment: departmentMap[getDepartmentCode(person.department)] || 'No Department',
          additionalDepartments: [],
          managerId: person.manager ? String(person.manager) : undefined
        },
        position: { x: 0, y: 0 },
      };
    });

    // Create edges after nodes are created
    const managerEdges: Edge[] = [];

    // Create a set of active user IDs for quick lookup
    const activeUserIds = new Set(activeUsers.map(u => String(u.id)));

    activeUsers.forEach(person => {
      if (person.manager) {
        const sourceId = String(person.manager);
        const targetId = String(person.id);

        // Only create edge if manager is also active
        if (activeUserIds.has(sourceId)) {

          managerEdges.push({
            id: `e${sourceId}-${targetId}`,
            source: sourceId,
            target: targetId,
            sourceHandle: 'source',
            targetHandle: 'target',
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#000000', strokeWidth: 2 },
          });
        }
      }
    });

    // Apply layout with specific settings
    dagreGraph.setGraph({
      rankdir: 'TB',
      nodesep: 50,
      ranksep: 100,
    });

    // Add nodes to dagre
    personNodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 240, height: 130 });
    });

    // Add edges to dagre
    managerEdges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate the layout
    dagre.layout(dagreGraph);

    // Retrieve positions from dagre
    const layoutedNodes = personNodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 120,
          y: nodeWithPosition.y - 65,
        },
      };
    });

    // Set the state
    setNodes(layoutedNodes);
    setEdges(managerEdges);
    setInitialized(true);
  }, [users, departments]);

  useEffect(() => {
    processData();
  }, [processData]);

  // Handle layout refresh
  const handleRefreshLayout = useCallback(() => {
    if (nodes.length === 0) return;

    // Reset the graph
    dagreGraph.setGraph({
      rankdir: 'TB',
      nodesep: 100,
      ranksep: 100,
      edgesep: 100,
    });

    // Add nodes to dagre
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 240, height: 130 });
    });

    // Add edges to dagre
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate the layout
    dagre.layout(dagreGraph);

    // Retrieve positions from dagre
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 120,
          y: nodeWithPosition.y - 65,
        },
      };
    });

    setNodes([...layoutedNodes]);
  }, [nodes, edges]);

  // Handle layout direction change
  const changeLayoutDirection = useCallback((direction: LayoutDirection) => {
    if (nodes.length === 0) return;

    // Reset the graph with new direction
    dagreGraph.setGraph({
      rankdir: direction,
      nodesep: 100,
      ranksep: 100,
      edgesep: 100,
    });

    // Add nodes to dagre
    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: 240, height: 130 });
    });

    // Add edges to dagre
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculate the layout
    dagre.layout(dagreGraph);

    // Retrieve positions from dagre
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - 120,
          y: nodeWithPosition.y - 65,
        },
      };
    });

    setNodes([...layoutedNodes]);
  }, [nodes, edges]);

  return (
    <div className="h-full relative" style={{
      height: '100%',
      width: '100%',
      position: 'relative',
      zIndex: 0
    }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.1}
        maxZoom={1.5}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#000000', strokeWidth: 2 },
        }}
        style={{ zIndex: 1 }}
        proOptions={{ hideAttribution: true }}
      >
        <Panel position="top-right" className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            className="bg-background"
            onClick={() => changeLayoutDirection('TB')}
            title="Top to Bottom layout"
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="bg-background"
            onClick={() => changeLayoutDirection('LR')}
            title="Left to Right layout"
          >
            <ArrowDown className="h-4 w-4 rotate-90" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="bg-background"
            onClick={handleRefreshLayout}
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Reset Layout
          </Button>
        </Panel>
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>
    </div>
  );
};
